import { createParticipantLayoutMediaStream } from "./participant-layout-dialog-media";
import { calculateAudioActivityVolume, updateSmoothedAudioActivityVolume } from "../../../utils/audio-activity-volume";

export const PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_INTERVAL_MS = 100;

const PARTICIPANT_LAYOUT_AUDIO_FFT_SIZE = 32;
const PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_THRESHOLD = 0.00001;
const PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_HOLD_MS = 1000;

let participantLayoutAudioContext = null;

function isActiveAudioTrack(track) {
  return Boolean(track && track.kind === "audio" && track.readyState !== "ended");
}

function getDialogLocalAudioTrack(dialog) {
  const micProducer = dialog?._micProducer;

  if (!micProducer || micProducer.closed || !isActiveAudioTrack(micProducer.track)) {
    return null;
  }

  return micProducer.track;
}

function getDialogRemoteAudioConsumers(dialog) {
  const consumers = Array.from(dialog?._consumers?.values?.() || []);

  return consumers.filter(consumer => {
    return !consumer?.closed && isActiveAudioTrack(consumer?.track) && Boolean(consumer?.appData?.peerId);
  });
}

function getAudioContextConstructor() {
  if (typeof window !== "undefined") {
    return window.AudioContext || window.webkitAudioContext;
  }

  if (typeof global !== "undefined") {
    return global.AudioContext || global.webkitAudioContext;
  }

  return null;
}

function getParticipantLayoutAudioContext() {
  if (typeof THREE !== "undefined" && THREE.AudioContext?.getContext) {
    try {
      return THREE.AudioContext.getContext();
    } catch (error) {
      return null;
    }
  }

  if (participantLayoutAudioContext) {
    return participantLayoutAudioContext;
  }

  const AudioContextConstructor = getAudioContextConstructor();

  if (!AudioContextConstructor) {
    return null;
  }

  try {
    participantLayoutAudioContext = new AudioContextConstructor();
  } catch (error) {
    return null;
  }

  return participantLayoutAudioContext;
}

function calculateParticipantLayoutAudioVolume(analyser, levels) {
  analyser.getByteTimeDomainData(levels);

  return calculateAudioActivityVolume(levels);
}

function createOwnerIdFilter(ownerIds) {
  if (!ownerIds) {
    return null;
  }

  return ownerIds instanceof Set ? ownerIds : new Set(ownerIds);
}

function shouldIncludeAudioTrackOwner(ownerId, ownerIdFilter) {
  return !ownerIdFilter || ownerIdFilter.has(ownerId);
}

export function getParticipantLayoutDialogAudioTracksByOwnerId({ dialog, mySessionId, ownerIds } = {}) {
  const audioTracksByOwnerId = {};
  const ownerIdFilter = createOwnerIdFilter(ownerIds);
  const localAudioTrack = getDialogLocalAudioTrack(dialog);

  if (localAudioTrack && mySessionId && shouldIncludeAudioTrackOwner(mySessionId, ownerIdFilter)) {
    audioTracksByOwnerId[mySessionId] = localAudioTrack;
  }

  getDialogRemoteAudioConsumers(dialog).forEach(consumer => {
    const ownerId = consumer.appData.peerId;

    if (!shouldIncludeAudioTrackOwner(ownerId, ownerIdFilter) || audioTracksByOwnerId[ownerId]) {
      return;
    }

    audioTracksByOwnerId[ownerId] = consumer.track;
  });

  return audioTracksByOwnerId;
}

export function createParticipantLayoutAudioActivityMonitor(track, audioContext = getParticipantLayoutAudioContext()) {
  if (!isActiveAudioTrack(track) || !audioContext?.createAnalyser || !audioContext?.createMediaStreamSource) {
    return null;
  }

  try {
    const mediaStream = createParticipantLayoutMediaStream(track);
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(mediaStream);

    analyser.fftSize = PARTICIPANT_LAYOUT_AUDIO_FFT_SIZE;
    source.connect(analyser);

    return {
      analyser,
      levels: new Uint8Array(analyser.fftSize || PARTICIPANT_LAYOUT_AUDIO_FFT_SIZE),
      source,
      track,
      volume: 0,
      prevVolume: 0,
      lastSpokeAt: 0
    };
  } catch (error) {
    return null;
  }
}

export function disposeParticipantLayoutAudioActivityMonitor(monitor) {
  if (!monitor?.source?.disconnect) {
    return;
  }

  try {
    monitor.source.disconnect();
  } catch (error) {
    // A disconnected Web Audio node throws in some browsers. The monitor is already disposable.
  }
}

export function sampleParticipantLayoutAudioActivityMonitor(monitor, now = Date.now()) {
  if (!monitor?.analyser || !monitor?.levels) {
    return false;
  }

  const nextVolume = calculateParticipantLayoutAudioVolume(monitor.analyser, monitor.levels);
  updateSmoothedAudioActivityVolume(monitor, nextVolume);

  if (monitor.volume > PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_THRESHOLD) {
    monitor.lastSpokeAt = now;
    return true;
  }

  return Boolean(monitor.lastSpokeAt && monitor.lastSpokeAt > now - PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_HOLD_MS);
}
